import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import {
  Box, Grid, Card, Typography, Button, TextField, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Menu
} from "@mui/material";
import { FormControl, InputLabel } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AppContext, siteName } from "../main";
import Navbar from "../layout/Navbar";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { BarChart, LineChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

import { saveDataDB, getDataDB, deleteDataDB } from "../api";

export async function saveProjectToDB(project) {
  return await saveDataDB({
    query: { ...project, date: moment() },
    keys: ["name"],
    resource: `${siteName}_project_manager`
  });
}

export async function saveTaskToDB(task) {
  return await saveDataDB({
    query: { ...task, date: moment() },
    keys: ["resource", "task"],
    resource: `${siteName}_task_manager_tasks`
  });
}

export async function loadAllProjects() {
  return await getDataDB({ query: { __safe__: null }, resource: `${siteName}_project_manager` });
}

export async function loadAllTasks() {
  return await getDataDB({ query: { __safe__: null }, resource: `${siteName}_task_manager_tasks` });
}

export default function ProjectDashboard() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectEdit, setSelectedProjectEdit] = useState(null);

  
  const [projectForm, setProjectForm] = useState({ name: "", priority: "H", pre: "", post: "" });
  const [taskForm, setTaskForm] = useState({
    resource: "", task: "", allocation: "", completion: "",
    status: "In Progress", tested: false, approved: false, deliveryDate: "", priority: "Mid"
  });  const [editingTask, setEditingTask] = useState(null);
  const [taskAnchorEl, setTaskAnchorEl] = useState(null);
  const [menuTask, setMenuTask] = useState(null);
  const [projectAnchorEl, setProjectAnchorEl] = useState(null);
  const [menuProject, setMenuProject] = useState(null);

  const [chartType, setChartType] = useState("Bar");
  const [metric, setMetric] = useState("Completed");
  const [groupBy, setGroupBy] = useState("Project");
  const [taskData, setTaskData] = useState([]);

  useEffect(() => {
    loadAllProjects().then(data => setProjects(data || []));
    loadAllTasks().then(data => setTasks(data || []));
  }, []);

  const resourceOptions = useMemo(() => {
    const uniqueResources = new Set();
    tasks.forEach(t => {
      if (t.resource) uniqueResources.add(t.resource);
    });
    return Array.from(uniqueResources);
  }, [tasks]);

  const allocationOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  const calculateProjectCompletion = (projectId) => {
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    if (!projectTasks.length) return 0;
    const total = projectTasks.reduce((sum, t) => sum + (parseFloat(t.allocation || 0) * parseFloat(t.completion || 0) / 100), 0);
    const allocationSum = projectTasks.reduce((sum, t) => sum + parseFloat(t.allocation || 0), 0);
    return allocationSum ? Math.round(total / allocationSum) : 0;
  };

  const handleSaveProject = async () => {
    if (!projectForm.name) return alert("Name required");
    await saveProjectToDB(projectForm);
    loadAllProjects().then(data => setProjects(data || []));
    setSelectedProject(null);
  };

  const handleDeleteProject = async (proj) => {
    const deletObject = {
      resource: `${siteName}_project_manager`,
      id: proj?._id,
    };
    // console.log("formInputvv-deletObject", deletObject);
    await deleteDataDB(deletObject)
    // await saveProjectToDB({ ...proj, hide: true });
    loadAllProjects().then(data => setProjects(data || []));
  };

  const handleSaveTask = async () => {
    if (!taskForm.resource || !taskForm.task) return alert("Missing fields");
    const taskRecord = {
      ...taskForm,
      projectId: selectedProjectEdit._id,
      projectName: selectedProjectEdit.name
    };
    await saveTaskToDB(taskRecord);
    loadAllTasks().then(data => setTasks(data || []));
    setSelectedProjectEdit(null);
  };

  const startEditing = (task) => setEditingTask(task);
  const cancelEditing = () => setEditingTask(null);
  const updateEditingField = (field, value) => setEditingTask(prev => ({ ...prev, [field]: value }));
  const saveRowUpdate = async () => {
    await saveTaskToDB(editingTask);
    loadAllTasks().then(data => setTasks(data || []));
    setEditingTask(null);
  };

  const handleDeleteTask = async (task) => {
    const deletObject = {
      resource:`${siteName}_task_manager_tasks`,
      id: task?._id,
    };
    // console.log("formInputvv-deletObject", deletObject);
    await deleteDataDB(deletObject)
    // await saveTaskToDB({ ...task, hide: true });
    loadAllTasks().then(data => setTasks(data || []));
  };

  const groupByResource = taskData.reduce((acc, task) => {
    const res = task.resource || "Unknown";
    if (!acc[res]) acc[res] = [];
    acc[res].push(task);
    return acc;
  }, {});

  const chartData = useMemo(() => {
    if (groupBy === "Project") {
      return projects.map(p => ({ name: p.name, value: metric === "Completed" ? p.completed : 0 }));
    }
    if (groupBy === "Priority") {
      const map = {};
      projects.forEach(p => {
        if (!map[p.priority]) map[p.priority] = { total: 0, count: 0 };
        map[p.priority].total += p.completed;
        map[p.priority].count += 1;
      });
      return Object.entries(map).map(([key, val]) => ({ name: key, value: (val.total / val.count).toFixed(1) }));
    }
    if (groupBy === "Resource") {
      const map = {};
      taskData.forEach(t => {
        if (!map[t.resource]) map[t.resource] = 0;
        map[t.resource] += parseFloat(t.allocation || 0);
      });
      return Object.entries(map).map(([key, val]) => ({ name: key, value: val }));
    }
    return [];
  }, [projects, taskData, groupBy, metric]);

  return (
    <Box p={3} sx={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Header />
      <div style={{
        paddingTop:70
      }} />

      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold" color="black">Goals Manager</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={2}>
            <Select fullWidth value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <MenuItem value="Project">Project</MenuItem>
              <MenuItem value="Priority">Priority</MenuItem>
              <MenuItem value="Resource">Resource</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Select fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
              <MenuItem value="Completed">Completed %</MenuItem>
              <MenuItem value="Allocation">Allocation %</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Select fullWidth value={chartType} onChange={e => setChartType(e.target.value)}>
              <MenuItem value="Bar">Bar</MenuItem>
              <MenuItem value="Line">Line</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Box>

      <Card sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" mb={2} color="black">Performance Overview</Typography>
        <ResponsiveContainer width="100%" height={300}>
          {chartType === "Bar" ? (
            <BarChart data={chartData}><XAxis dataKey="name" stroke="black" /><YAxis stroke="black" /><Tooltip /><Bar dataKey="value" fill="#1976d2" /></BarChart>
          ) : (
            <LineChart data={chartData}><XAxis dataKey="name" stroke="black" /><YAxis stroke="black" /><Tooltip /><Line dataKey="value" stroke="#1976d2" strokeWidth={3} /></LineChart>
          )}
        </ResponsiveContainer>
      </Card>

      <Card sx={{ p: 3, mb: 4 }}>


        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Projects</Typography>
          <Button
    variant="contained"
    onClick={() => {
      setProjectForm({ name: "", priority: "H", pre: "", post: "" });
      setSelectedProject("NEW");
    }}
  >
    Add Project
  </Button>

<Dialog open={Boolean(selectedProject)} onClose={() => setSelectedProject(null)}>
  <DialogTitle sx={{ color: "black" }}>
    {selectedProject === "NEW" ? "Add Project" : "Edit Project"}
  </DialogTitle>

  <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <TextField
      label="Project Name"
      value={projectForm.name}
      onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
      sx={{ minWidth: 300 }}
    />
<TextField
  label="Pre Production (MVP)"
  type="date"
  value={projectForm.pre}
  onChange={(e) =>
    setProjectForm({ ...projectForm, pre: e.target.value })
  }
  sx={{
    minWidth: 300,
    '& input': {
      padding: '10.5px 14px',
    },
  }}
  InputLabelProps={{ shrink: true }}
/>

<TextField
  label="Post Production"
  type="date"
  value={projectForm.post}
  onChange={(e) =>
    setProjectForm({ ...projectForm, post: e.target.value })
  }
  sx={{
    minWidth: 300,
    '& input': {
      padding: '10.5px 14px',
    },
  }}
  InputLabelProps={{ shrink: true }}
/>

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel>Priority</InputLabel>
      <Select
        value={projectForm.priority}
        onChange={(e) => setProjectForm({ ...projectForm, priority: e.target.value })}
      >
        <MenuItem value="H">High</MenuItem>
        <MenuItem value="M">Mid</MenuItem>
        <MenuItem value="L">Low</MenuItem>
      </Select>
    </FormControl>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setSelectedProject(null)}>Cancel</Button>
    <Button onClick={handleSaveProject}>Save</Button>
  </DialogActions>
</Dialog>





        </Box>



        <TableContainer>
          <Table>
            <TableHead>
              <TableRow><TableCell>Project</TableCell><TableCell>Priority</TableCell><TableCell>% Completed</TableCell><TableCell>Pre</TableCell><TableCell>Post</TableCell><TableCell>Action</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {projects.map(proj => (
                <TableRow key={proj._id}>
                  <TableCell>{proj.name}</TableCell>
                  <TableCell>{proj.priority}</TableCell>
                  <TableCell>{calculateProjectCompletion(proj._id)}%</TableCell>
                  <TableCell>{proj.pre}</TableCell>
                  <TableCell>{proj.post}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => { setProjectAnchorEl(e.currentTarget); setMenuProject(proj); }}><MoreVertIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu anchorEl={projectAnchorEl} open={Boolean(projectAnchorEl)} onClose={() => setProjectAnchorEl(null)}>
        <MenuItem onClick={() => { setSelectedProject(menuProject); setProjectForm(menuProject); setProjectAnchorEl(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => { handleDeleteProject(menuProject); setProjectAnchorEl(null); }}>Delete</MenuItem>
        <MenuItem onClick={() => { setTaskForm({}); setSelectedProjectEdit(menuProject); setProjectAnchorEl(null); }}>Add Task</MenuItem>
      </Menu>



      <Dialog open={Boolean(selectedProjectEdit?.name)} onClose={() => setSelectedProjectEdit(null)}>
  <DialogTitle sx={{ color: 'black' }}>Add Task for {selectedProjectEdit?.name}</DialogTitle>
  <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

  <FormControl sx={{ minWidth: 300 }}>
  {/* <InputLabel sx={{ color: 'black' }}>Resource</InputLabel> */}

  <Autocomplete
  freeSolo
  options={resourceOptions}
  value={taskForm.resource}
  onChange={(event, newValue) => {
    setTaskForm({ ...taskForm, resource: newValue });
  }}
  onInputChange={(event, newInputValue) => {
    setTaskForm({ ...taskForm, resource: newInputValue });
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Resource"
      sx={{
        minWidth: 300,
        "& .MuiInputLabel-root": { color: "black" },
        input: { color: "black" }
      }}
    />
  )}
/>



</FormControl>


    <TextField
      label="Task Name"
      value={taskForm.task}
      onChange={e => setTaskForm({ ...taskForm, task: e.target.value })}
      sx={{ minWidth: 300, "& .MuiInputLabel-root": { color: 'black' }, input: { color: 'black' } }}
    />

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel sx={{ color: 'black' }}>Allocation %</InputLabel>
      <Select
        value={taskForm.allocation}
        onChange={e => setTaskForm({ ...taskForm, allocation: e.target.value })}
        sx={{ color: 'black' }}
      >
        <MenuItem value=""><em>Select Allocation</em></MenuItem>
        {allocationOptions.map(val => (<MenuItem key={val} value={val}>{val}%</MenuItem>))}
      </Select>
    </FormControl>

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel sx={{ color: 'black' }}>Completion %</InputLabel>
      <Select
        value={taskForm.completion}
        onChange={e => setTaskForm({ ...taskForm, completion: e.target.value })}
        sx={{ color: 'black' }}
      >
        <MenuItem value=""><em>Select Completion %</em></MenuItem>
        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(val => (
          <MenuItem key={val} value={val}>{val}%</MenuItem>
        ))}
      </Select>
    </FormControl>

  </DialogContent>
  <DialogActions>
    <Button onClick={() => setSelectedProjectEdit(null)}>Cancel</Button>
    <Button onClick={handleSaveTask}>Save Task</Button>
  </DialogActions>
</Dialog>



      <Card sx={{ p: 3 }}>
        <Typography variant="h6" mb={2} color="black">Resource Summary</Typography>
        {Object.entries(tasks.reduce((acc, t) => { const key = t.resource || "Unknown"; if (!acc[key]) acc[key] = []; acc[key].push(t); return acc }, {})).map(([res, resTasks]) => (
          <Accordion key={res}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>{res}</Typography></AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Task</TableCell>
                    <TableCell>Allocation</TableCell>
                    <TableCell>Completion</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tested</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Delivery Date</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resTasks.map(task => (
                    <TableRow key={task._id}>
                      <TableCell>{task.projectName}</TableCell>
                      <TableCell>{task.task}</TableCell>
                      <TableCell>{task.allocation}%</TableCell>
                      <TableCell>{task.completion}%</TableCell>
                      <TableCell>{task.status}</TableCell>
                      <TableCell>{task.tested ? "✔" : "❌"}</TableCell>
                      <TableCell>{task.approved ? "✔" : "❌"}</TableCell>
                      <TableCell>{task.deliveryDate}</TableCell>
                      <TableCell>{task.priority}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { setTaskAnchorEl(e.currentTarget); setMenuTask(task); }}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
      </Card>

      <Menu anchorEl={taskAnchorEl} open={Boolean(taskAnchorEl)} onClose={() => setTaskAnchorEl(null)}>
        <MenuItem onClick={() => { startEditing(menuTask); setTaskAnchorEl(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => { handleDeleteTask(menuTask); setTaskAnchorEl(null); }}>Delete</MenuItem>
      </Menu>

      <Dialog open={Boolean(editingTask)} onClose={cancelEditing}>
  <DialogTitle sx={{ color: 'black' }}>Edit Task</DialogTitle>
  <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

    <TextField 
      label="Task Name" 
      placeholder="Enter task name" 
      value={editingTask?.task} 
      onChange={e => updateEditingField("task", e.target.value)} 
      sx={{ minWidth: 300, "& .MuiInputLabel-root": { color: 'black' }, input: { color: 'black' } }}
    />

    <TextField 
      label="Allocation %" 
      type="number" 
      placeholder="Enter allocation" 
      value={editingTask?.allocation} 
      onChange={e => updateEditingField("allocation", e.target.value)} 
      sx={{ minWidth: 300, "& .MuiInputLabel-root": { color: 'black' }, input: { color: 'black' } }}
    />

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel sx={{ color: 'black' }}>Completion %</InputLabel>
      <Select
        value={editingTask?.completion}
        onChange={e => updateEditingField("completion", e.target.value)}
        sx={{ color: 'black' }}
      >
        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(val => (
          <MenuItem key={val} value={val}>{val}%</MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel sx={{ color: 'black' }}>Status</InputLabel>
      <Select
        value={editingTask?.status}
        onChange={e => updateEditingField("status", e.target.value)}
        sx={{ color: 'black' }}
      >
        <MenuItem value="In Progress">In Progress</MenuItem>
        <MenuItem value="In Review">In Review</MenuItem>
        <MenuItem value="Done">Done</MenuItem>
      </Select>
    </FormControl>

    <FormControl sx={{ minWidth: 300 }}>
      <InputLabel sx={{ color: 'black' }}>Priority</InputLabel>
      <Select
        value={editingTask?.priority}
        onChange={e => updateEditingField("priority", e.target.value)}
        sx={{ color: 'black' }}
      >
        <MenuItem value="High">High</MenuItem>
        <MenuItem value="Mid">Mid</MenuItem>
        <MenuItem value="Low">Low</MenuItem>
      </Select>
    </FormControl>

    <TextField 
      label="Delivery Date" 
      type="date" 
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 300, "& .MuiInputLabel-root": { color: 'black' }, input: { color: 'black' } }}
      value={editingTask?.deliveryDate}
      onChange={e => updateEditingField("deliveryDate", e.target.value)}
    />

    <div>
      <label style={{ color: 'black' }}>
        <input 
          type="checkbox" 
          checked={editingTask?.tested} 
          onChange={e => updateEditingField("tested", e.target.checked)} 
        /> Tested
      </label>
    </div>

    <div>
      <label style={{ color: 'black' }}>
        <input 
          type="checkbox" 
          checked={editingTask?.approved} 
          onChange={e => updateEditingField("approved", e.target.checked)} 
        /> Approved
      </label>
    </div>

  </DialogContent>
  <DialogActions>
    <Button onClick={cancelEditing}>Cancel</Button>
    <Button onClick={saveRowUpdate}>Save</Button>
  </DialogActions>
</Dialog>



    </Box>
  );
}

const Header = () => (
    <header id="header-wrap" style={{}}>
                    <Navbar
                      navEdit={true}
                    />
                         </header>
);
const Footer = () => (<Box sx={{ textAlign: "center", py: 3, mt: 5, borderTop: "1px solid #ddd" }}><Typography variant="body2">© {new Date().getFullYear()} Vare Technologies</Typography></Box>);
